/**
 * Où poser la main du coach : le bout de l'index touche le bas de l'élément à toucher, la main en dessous. Si elle
 * sortirait de l'écran (touches du dock), elle passe au-dessus, retournée, l'index vers le bas.
 */

/** Taille de la main dessinée (px) et position du bout de l'index dans ce cadre, main droite. */
export const COACH_HAND_SIZE = { width: 40, height: 48 };
export const COACH_HAND_TIP = { x: 16, y: 2 };
/** De combien le bout de l'index entre dans l'élément montré. */
const OVERLAP = 6;
/** Marge gardée avec le bord de l'écran. */
const MARGIN = 8;

export type CoachRect = { left: number; top: number; width: number; height: number };
export type CoachHandPlacement = { left: number; top: number; flipped: boolean };

export function coachHandPlacement(
  rect: CoachRect,
  viewport: { width: number; height: number },
): CoachHandPlacement | null {
  if (rect.width <= 0 || rect.height <= 0) return null;
  const bottom = rect.top + rect.height;
  // hors de l'écran : rien à montrer
  if (bottom <= 0 || rect.top >= viewport.height) return null;
  if (rect.left + rect.width <= 0 || rect.left >= viewport.width) return null;

  const { width, height } = COACH_HAND_SIZE;
  const cx = rect.left + rect.width / 2;
  const below = bottom - OVERLAP;
  const flipped = below - COACH_HAND_TIP.y + height + MARGIN > viewport.height;
  // retournée d'un demi-tour autour de son centre, le bout de l'index est en (width − x, height − y)
  const tipX = flipped ? width - COACH_HAND_TIP.x : COACH_HAND_TIP.x;
  const tipY = flipped ? height - COACH_HAND_TIP.y : COACH_HAND_TIP.y;
  const tipAt = flipped ? rect.top + OVERLAP : below;
  const left = Math.min(Math.max(cx - tipX, MARGIN), viewport.width - width - MARGIN);
  return { left, top: tipAt - tipY, flipped };
}

/*
 * La main ne SAUTE pas d'un élément à l'autre : elle y glisse, en se soulevant un peu au passage,
 * comme une main qui quitte une touche pour aller en toucher une autre (retour de Philippe du
 * 5/11/2026). Tout est pur ici : le composant ne fait que lire l'heure et poser le `transform`.
 */
export const HAND_TRAVEL = {
  /** durée d'un trajet (ms), bornée : un petit déplacement ne traîne pas, un grand ne file pas */
  min: 240,
  max: 620,
  /** vitesse de croisière (px/ms) */
  speed: 1.6,
  /** de combien la main se soulève au milieu du trajet (px, plafonné) */
  lift: 26,
  /** l'apparition : elle arrive en grossissant, sans venir de nulle part */
  enter: 240,
};

function dist(a: CoachHandPlacement, b: CoachHandPlacement): number {
  return Math.hypot(b.left - a.left, b.top - a.top);
}

/** Combien de temps pour aller d'une pose à l'autre. */
export function handTravelMs(from: CoachHandPlacement, to: CoachHandPlacement): number {
  const d = dist(from, to);
  return Math.min(HAND_TRAVEL.max, Math.max(HAND_TRAVEL.min, d / HAND_TRAVEL.speed));
}

/** Départ et arrivée en douceur. */
function ease(p: number): number {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
}

/** Où est la main au cours du trajet (0 = départ, 1 = arrivée) ; elle se retourne à mi-chemin. */
export function handTravelAt(
  from: CoachHandPlacement,
  to: CoachHandPlacement,
  p: number,
): CoachHandPlacement {
  const e = ease(p);
  const lift = Math.sin(Math.PI * e) * Math.min(HAND_TRAVEL.lift, dist(from, to) * 0.18);
  return {
    left: from.left + (to.left - from.left) * e,
    top: from.top + (to.top - from.top) * e - lift,
    flipped: e < 0.5 ? from.flipped : to.flipped,
  };
}
